/*
 * Newsletter sign-up -> Klaviyo's public client API (decision 2026-10-30: theme forms).
 * Ported from lunthra-titanium app/lib/klaviyo.server.ts and api.newsletter: the same endpoint, payload and email
 * limit, sent from the browser with the public company id (no secret involved). POST /client/subscriptions/ to the
 * double opt-in list; Klaviyo sends the confirmation email. The contact page uses Shopify's native contact form
 * instead (decision 2026-10-30), for Shopify's server-side spam protection.
 *
 * Bot handling here is a honeypot field (a filled one fakes success and sends nothing); double opt-in keeps
 * unconfirmed addresses off the list.
 *
 * The forms render with their submit button disabled and method="post"; this script enables them once bound, so a
 * submission without JavaScript can never put the fields in the page URL. Loaded once, from layout/theme.liquid.
 */
(() => {
  const REVISION = '2026-04-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MAX_EMAIL = 254;

  const post = async (path, companyId, body) => {
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: REVISION },
          body: JSON.stringify(body),
        },
      );
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const subscription = (email, listId, source) => ({
    data: {
      type: 'subscription',
      attributes: {
        custom_source: source,
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    },
  });

  // Returns [message, field name to focus], or null when the email is acceptable.
  const problem = (text, email) => {
    if (!email) return [text.missing, 'email'];
    if (!EMAIL_RE.test(email) || email.length > MAX_EMAIL) return [text.email, 'email'];
    return null;
  };

  const wire = (form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = 'true';

    const root = form.closest('[data-form-root]');
    const button = form.querySelector('[type="submit"]');
    const errorLine = root.querySelector('[data-form-error]');
    const statusLine = root.querySelector('[data-form-status]');
    const text = JSON.parse(form.dataset.strings);
    const idleLabel = button.textContent;
    let submitting = false;

    const setBusy = (busy) => {
      submitting = busy;
      button.disabled = busy;
      button.textContent = busy ? text.busy : idleLabel;
    };

    const showError = (message, fieldName) => {
      errorLine.textContent = message;
      errorLine.hidden = !message;
      form.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
      const field = fieldName && form.elements[fieldName];
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', errorLine.id);
        field.focus();
      }
    };

    const succeed = () => {
      form.hidden = true;
      root.querySelectorAll('[data-form-hide-on-success]').forEach((element) => {
        element.hidden = true;
      });
      root.querySelectorAll('[data-form-show-on-success]').forEach((element) => {
        element.hidden = false;
      });
      statusLine.textContent = text.success;
      statusLine.hidden = false;
      statusLine.focus();
      // The sign-up pop-up listens for this to remember the visitor joined and to save the welcome code to the bag.
      root.dispatchEvent(new CustomEvent('plate:joined', { bubbles: true }));
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitting) return;
      showError('');
      const value = (name) => (form.elements[name]?.value ?? '').trim();

      // Honeypot filled: answer as if it worked, send nothing.
      if (value(form.dataset.honeypot)) return succeed();

      const email = value('email');
      const found = problem(text, email);
      if (found) return showError(...found);

      setBusy(true);
      try {
        const { company, list, source } = form.dataset;
        if (await post('subscriptions', company, subscription(email, list, source))) return succeed();
        showError(text.failed);
      } finally {
        setBusy(false);
      }
    });

    button.disabled = false;
  };

  document.querySelectorAll('form[data-klaviyo-form="newsletter"]').forEach(wire);
})();
